import React, {
  useRef,
  useId,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import { ChevronDownIc } from '@dsch/dd-icons';
import * as Styled from 'components/ToolkitV2/Accordion/Accordion.styled';
import { Spacer } from 'helpers/genericStyles';
import type { AccordionProps } from 'components/ToolkitV2/Accordion/Accordion.typed';
import withDefaultProps from 'components/ToolkitV2/hoc/withDefaultProps';
import withStringOrComponent from 'components/ToolkitV2/hoc/withStringOrComponent';
import { useTheme } from 'styled-components';
import { Title } from 'components/Toolkit/InfoTitle/Title/Title';

type AccordionComponent = ForwardRefExoticComponent<
  AccordionProps & RefAttributes<HTMLDivElement>
> & {
  Heading: React.ComponentType<any>;
};

const Accordion = forwardRef<HTMLDivElement, AccordionProps>((props, ref) => {
  const {
    isOpen,
    onChange,
    header,
    headerSpacerTop = <Spacer marginBottom={'M16'} />,
    headerSpacerBottom = <Spacer marginBottom={'M16'} />,
    bodySpacer = <Spacer marginBottom={'M24'} />,
    content,
    showDivider = true,
    dataTracking,
    accordionName,
  } = props;

  const heading = withStringOrComponent(header, Accordion.Heading);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();
  const uniqueId = useId();

  const onKeyDown = (event: React.KeyboardEvent) => {
    const key = event.code.toLowerCase();
    const isFocussed = document.activeElement === headerRef.current;

    if (isFocussed && (key === 'enter' || key === 'space')) {
      event.preventDefault();
      onChange();
    }
  };

  return (
    <Styled.Accordion ref={ref} showDivider={showDivider}>
      {headerSpacerTop}
      <Styled.Header
        ref={headerRef}
        tabIndex={0}
        role="button"
        aria-expanded={isOpen}
        aria-controls={accordionName}
        aria-labelledby={uniqueId}
        onClick={onChange}
        onKeyDown={onKeyDown}
        data-tracking={dataTracking}
        data-testid={'accordion-header'}
      >
        <div id={uniqueId}>{heading}</div>

        <Styled.ChevronIconWrapper isOpen={isOpen}>
          <ChevronDownIc
            width="20"
            height="20"
            color={theme.colors.GREY_DARKER}
            data-testid={'accordion-chevron'}
          />
        </Styled.ChevronIconWrapper>
      </Styled.Header>
      {headerSpacerBottom}
      <Styled.Body
        data-testid={'accordion-body'}
        isOpen={isOpen}
        id={accordionName}
      >
        {content}
      </Styled.Body>
      {bodySpacer}
    </Styled.Accordion>
  );
}) as AccordionComponent;

Accordion.Heading = withDefaultProps(Title, {
  flip: false,
  shouldTruncate: false,
  variant: 'DEFAULT',
});

Accordion.displayName = 'Accordion';

export { Accordion };
